import { useIsAuthenticated } from '@/hooks';
import {
  useGetUserProfileQuery,
  useGetUserWalletsQuery,
} from '@/store/api/userApi';
import { formatCurrency } from '@/utils';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { RefreshCw } from 'lucide-react';
import { GiHamburgerMenu } from 'react-icons/gi';

import { Link, useLocation, useNavigate } from 'react-router-dom';
import SidebarMenu from './SidebarMenu';
import { useState } from 'react';

type Props = {};

const backButtonRoutes = ['/marketdetails', '/fullmarket/'];

function Header({}: Props) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isAuthenticated = useIsAuthenticated();

  const showBackButton = backButtonRoutes.some((route) =>
    pathname.startsWith(route),
  );

  return (
    <>
      {/* Header */}
      <div className="h-[3.125rem] py-1.5 px-1.5 sm:px-2 sticky top-0 z-30 flex justify-between items-center text-11 bg-primary">
        {/* Left Section */}
        <div className="text-[1.75rem] flex items-center gap-2 min-w-0">
          {showBackButton ? (
            <button
              type="button"
              aria-label="Go back"
              onClick={() => navigate(-1)}
              className="flex h-8 w-8 shrink-0 items-center justify-center"
            >
              <i className="icon-arrow-down rotate-90 text-xl" />
            </button>
          ) : (
            isAuthenticated && (
              <button
                type="button"
                aria-label="Open menu"
                onClick={() => setSidebarOpen(true)}
                className="flex h-8 w-8 shrink-0 items-center justify-center"
              >
                <GiHamburgerMenu />
              </button>
            )
          )}

          {/* Logo */}
          <Link
            to="/"
            className="flex h-[3.125rem] items-center overflow-hidden"
          >
            <img
              src="/images/logo-9winbet-v2.webp"
              alt="9WINBET"
              className="h-[3rem] w-auto max-w-[8rem] object-contain"
            />
          </Link>
        </div>

        {/* Right Section */}
        {isAuthenticated ? (
          <HeaderBalance />
        ) : (
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            {/* Signup */}
            <Link
              to="/register"
              className="flex h-[2rem] items-center justify-center rounded bg-black/40 px-2.5 sm:px-4 text-[0.938rem] sm:text-base font-semibold text-white transition-opacity hover:opacity-90"
            >
              <i className="icon-SignUp mr-1.5 text-base leading-none" />
              SignUp
            </Link>

            {/* Login */}
            <Link
              to="/login"
              className="flex h-[2rem] items-center justify-center rounded bg-[#e20000] px-3 sm:px-4 text-[0.938rem] sm:text-base font-semibold text-white transition-opacity hover:opacity-90"
            >
              <i className="icon-login mr-1.5 text-base leading-none" />
              Login
            </Link>
          </div>
        )}
      </div>

      {/* Sidebar */}
      <SidebarMenu
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
    </>
  );
}

export default Header;

const HeaderBalance = () => {
  const {
    data: profile,
    isLoading: isProfileLoading,
    refetch: refetchProfile,
  } = useGetUserProfileQuery();

  const {
    data: wallet,
    isLoading: isWalletLoading,
    refetch: refetchWallet,
  } = useGetUserWalletsQuery(undefined, {
    pollingInterval: 1000,
  });

  const [isRefreshing, setIsRefreshing] = useState(false);
  const currencySymbol = useCurrencySymbol();

  const exposure = Math.abs(wallet?.exposure ?? 0);

  const mainBalance = (wallet?.mainBalance ?? 0) - exposure;

  const bonusBalance = wallet?.bonusBalance ?? 0;

  const username = profile?.username ?? '';

  const handleRefresh = async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);

    try {
      await Promise.allSettled([
        refetchProfile(),
        refetchWallet(),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="flex items-center justify-between shrink-0">
      {/* Loading */}
      {isProfileLoading || isWalletLoading || isRefreshing ? (
        <div className="flex items-center gap-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <span
              key={i}
              className="h-1.5 w-1.5 rounded-full bg-black animate-dot-wave"
              style={{
                animationDelay: `${i * 100}ms`,
              }}
            />
          ))}
        </div>
      ) : (
        <div className="flex h-full w-full max-w-xs items-center justify-between gap-2 py-0.5 text-[0.844rem] leading-none">
          {/* User / Bonus */}
          <div className="min-w-0">
            <p className="mb-1 truncate text-header-text">
              {username ? `@${username}` : ''}
            </p>

            <p>
              <span className="mr-1 font-bold text-black/50">
                Bonus
              </span>

              <span>
                {formatCurrency(bonusBalance)}
              </span>
            </p>
          </div>

          {/* Balance / Exposure */}
          <div className="shrink-0 text-right">
            <p className="mb-1">
              <span className="mr-1 font-bold text-black/50">
                {currencySymbol}
              </span>

              <span>
                {formatCurrency(mainBalance)}
              </span>
            </p>

            <p>
              <span className="mr-1 font-bold text-black/50">
                Exp
              </span>

              <span className="text-rose-600">
                ({formatCurrency(wallet?.exposure)})
              </span>
            </p>
          </div>
        </div>
      )}

      {/* Refresh */}
      <button
        type="button"
        onClick={handleRefresh}
        disabled={isRefreshing}
        aria-label="Refresh balance"
        className="ml-1 mr-1 flex cursor-pointer items-center justify-center disabled:cursor-not-allowed disabled:opacity-70"
      >
        <RefreshCw
          strokeWidth={2.8}
          className={`ml-2.5 mr-1 size-[1.4rem] ${
            isRefreshing ? 'animate-spin' : 'rotate-[130deg]'
          }`}
        />
      </button>
    </div>
  );
};